import React, { useEffect, useState } from 'react';
import MediaUploader from '../../components/dashboard/MediaUploader';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';

const CreatorProfile = () => {
    const [profile, setProfile] = useState({
        name: '',
        bio: '',
        instagram: '',
        website: '',
        paypalEmail: '',
        avatar: null,
        channelVideo: null,
        referralLink: '',
        slug: { current: '' }
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchFullProfile();
    }, []);

    const fetchFullProfile = async () => {
        const token = localStorage.getItem('paradise_token');
        try {
            const res = await fetch(`${API_BASE}/api/creator/profile`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setProfile({
                    name: data.name || '',
                    bio: data.bio || '',
                    instagram: data.links?.find(l => l.title === 'Instagram')?.url || '',
                    website: data.links?.find(l => l.title === 'Website')?.url || '',
                    paypalEmail: data.paypalEmail || '',
                    avatar: data.avatar,
                    channelVideo: data.channelVideo || null,
                    referralLink: data.referralLink || '',
                    slug: data.slug || { current: '' }
                });
            }
        } catch (e) { console.error(e) }
        setLoading(false);
    }

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);

        const token = localStorage.getItem('paradise_token');
        const links = [];
        if (profile.instagram) links.push({ title: 'Instagram', url: profile.instagram });
        if (profile.website) links.push({ title: 'Website', url: profile.website });

        try {
            const res = await fetch(`${API_BASE}/api/creator/profile`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: profile.name,
                    bio: profile.bio,
                    links,
                    avatar: profile.avatar,
                    channelVideo: profile.channelVideo,
                    referralLink: profile.referralLink,
                    paypalEmail: profile.paypalEmail
                })
            });

            if (res.ok) {
                setMessage({ type: 'success', text: 'Profile saved successfully.' });
            } else {
                setMessage({ type: 'error', text: 'Failed to save profile.' });
            }
        } catch (e) {
            setMessage({ type: 'error', text: 'Connection error.' });
        } finally {
            setSaving(false);
        }
    };

    const handleCopyLink = () => {
        const code = profile.referralLink || profile.slug?.current;
        if (!code) return;
        const link = `https://www.welcometoparadise.world/?ref=${code}`;
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleAvatarUpload = (result) => {
        if (result && result.variants) {
            setProfile(prev => ({
                ...prev, avatar: {
                    _type: 'cloudflareImage',
                    id: result.cloudflareId,
                    variants: result.variants
                }
            }));
        } else if (result.cloudflareId) {
            setProfile(prev => ({ ...prev, avatar: { _type: 'cloudflareImage', id: result.cloudflareId } }));
        }
    };

    const handleVideoUpload = (result) => {
        if (result && result.cloudflareId) {
            setProfile(prev => ({
                ...prev,
                channelVideo: {
                    _type: 'cloudflareStreamData',
                    uid: result.cloudflareId,
                    status: 'ready'
                }
            }));
        }
    };

    if (loading) return <div>Loading Profile...</div>;

    const activeReferralCode = profile.referralLink || profile.slug?.current || 'PENDING';

    return (
        <div className="max-w-4xl">
            <h2 className="text-2xl font-bold mb-6 italic uppercase tracking-widest font-mono">Profile Settings</h2>

            <form onSubmit={handleSave} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left Column: Basic Info */}
                    <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 space-y-6">
                        <div className="flex items-center space-x-6 mb-4">
                            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center overflow-hidden border border-gray-100 shadow-inner">
                                {profile.avatar && (profile.avatar.variants?.[0] || profile.avatar.id) ? (
                                    <img
                                        src={profile.avatar.variants?.[0] || `https://imagedelivery.net/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH}/${profile.avatar.id}/public`}
                                        alt="Avatar"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-gray-300 font-mono text-[10px] uppercase tracking-tighter">No Photo</span>
                                )}
                            </div>
                            <div className="flex-1">
                                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Profile Image</label>
                                <MediaUploader
                                    onUploadComplete={handleAvatarUpload}
                                    accept="image/*"
                                    label="Upload Avatar"
                                    compact={true}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Display Name</label>
                            <input
                                type="text"
                                value={profile.name}
                                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                className="w-full bg-[#fcfcfc] p-3 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-black outline-none transition-all"
                                placeholder="Your artist/display name"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Bio</label>
                            <textarea
                                rows="5"
                                value={profile.bio}
                                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                                className="w-full bg-[#fcfcfc] p-3 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-black outline-none transition-all"
                                placeholder="A brief description of you and your work..."
                            ></textarea>
                        </div>

                        <div className="space-y-3">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Payout Information</label>
                            <input
                                type="email"
                                placeholder="PayPal Email Address"
                                value={profile.paypalEmail}
                                onChange={(e) => setProfile({ ...profile, paypalEmail: e.target.value })}
                                className="w-full bg-[#fcfcfc] p-3 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-black outline-none transition-all"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Social Links</label>
                            <input
                                type="text"
                                placeholder="Instagram URL"
                                value={profile.instagram}
                                onChange={(e) => setProfile({ ...profile, instagram: e.target.value })}
                                className="w-full bg-[#fcfcfc] p-3 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-black outline-none transition-all"
                            />
                            <input
                                type="text"
                                placeholder="Website URL"
                                value={profile.website}
                                onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                                className="w-full bg-[#fcfcfc] p-3 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-black outline-none transition-all"
                            />
                        </div>
                    </div>

                    {/* Right Column: Channel Video & Referral */}
                    <div className="space-y-8">
                        {/* Channel Video */}
                        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 flex flex-col">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">TV Channel Video</label>
                            <div className="flex-1 flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-100 rounded-lg bg-gray-50/50 mb-6 min-h-[160px]">
                                {profile.channelVideo ? (
                                    <div className="w-full aspect-video bg-black rounded overflow-hidden shadow-lg relative group">
                                        <video
                                            src={`https://videodelivery.net/${profile.channelVideo.uid}/downloads/default.mp4`}
                                            className="w-full h-full object-cover opacity-80"
                                            poster={`https://videodelivery.net/${profile.channelVideo.uid}/thumbnails/thumbnail.jpg`}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <span className="text-white text-[10px] font-bold uppercase tracking-widest">Video Uploaded</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center">
                                        <div className="text-gray-300 font-mono text-[9px] uppercase tracking-widest">No Video Assigned</div>
                                    </div>
                                )}
                            </div>
                            <MediaUploader
                                onUploadComplete={handleVideoUpload}
                                accept="video/*"
                                label={profile.channelVideo ? "Replace Channel Video" : "Upload Channel Video"}
                            />
                        </div>

                        {/* Referral Section */}
                        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">Referral Program</label>
                            <p className="text-[11px] text-gray-500 mb-6 leading-relaxed">
                                Share your unique link to track membership signups.
                                Custom codes override your display name slug.
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[9px] font-bold uppercase tracking-tighter text-gray-400 mb-1">Custom Code</label>
                                    <input
                                        type="text"
                                        placeholder={profile.slug?.current || "e.g. your-name"}
                                        value={profile.referralLink}
                                        onChange={(e) => setProfile({ ...profile, referralLink: e.target.value })}
                                        className="w-full bg-[#fcfcfc] p-3 border border-gray-200 rounded text-sm font-mono focus:ring-1 focus:ring-black outline-none transition-all"
                                    />
                                </div>

                                <div className="bg-emerald-50/50 border border-emerald-100 p-4 rounded-lg">
                                    <label className="block text-[9px] font-bold uppercase tracking-tighter text-emerald-600/60 mb-2">My Sharing Link</label>
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 overflow-hidden">
                                            <code className="text-[10px] text-emerald-700 font-mono truncate block">
                                                https://www.welcometoparadise.world/?ref={activeReferralCode}
                                            </code>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCopyLink}
                                            className="px-3 py-1.5 bg-emerald-600 text-white text-[9px] font-bold uppercase tracking-widest rounded hover:bg-emerald-700 transition-colors shadow-sm whitespace-nowrap"
                                        >
                                            {copied ? 'Copied!' : 'Copy Link'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {message && (
                    <div className={`p-4 rounded text-[10px] font-bold uppercase tracking-widest border ${message.type === 'success' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-red-50 text-red-500 border-red-100'}`}>
                        {message.text}
                    </div>
                )}

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="bg-black text-white px-10 py-4 rounded font-mono text-xs font-bold uppercase tracking-[0.2em] hover:bg-zinc-800 disabled:opacity-50 transition-all shadow-lg active:scale-95"
                    >
                        {saving ? 'Syncing...' : 'Update Profile'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreatorProfile;
